import { useCallback, useEffect, useMemo, useState } from 'react';

type ListItem<T> = T & { id: string };

export interface ListSelector<ListItemType> {
  listItems: ListItem<ListItemType>[];
  selectedListItem: ListItem<ListItemType> | null;
  handleSelect: (listItem: ListItem<ListItemType> | null) => void;
}

type Params<ListItemType> = {
  listItems: ListItem<ListItemType>[];
  onSelect?: (listItem: ListItem<ListItemType> | null) => void;
};

/**
 * "useListController" - hook provides methods and fields which cover default "select from list" functionality
 * such as: payment instruments select, car was select, etc.
 * "useListController" is not depends on any UI implementation and can be used everywhere.
 * "useListController" implements basic features: get list's items, get selected item, handle select item
 * @param params
 */
export const useListController = <ListItemType>(params: Params<ListItemType>): ListSelector<ListItemType> => {
  const { listItems, onSelect } = params;
  const [selectedListItem, setSelectedListItem] = useState<ListItem<ListItemType> | null>(null);

  const handleSelect = useCallback(
    (listItem: ListItem<ListItemType> | null) => {
      setSelectedListItem(listItem);
      onSelect?.(listItem);
    },
    [onSelect],
  );

  useEffect(() => {
    if (!selectedListItem || listItems.find((item) => item.id === selectedListItem.id)) {
      return;
    }
    setSelectedListItem(null);
  }, [listItems, selectedListItem]);

  return useMemo(() => ({ selectedListItem, handleSelect, listItems }), [handleSelect, listItems, selectedListItem]);
};
